/*!
 * CHIEF - Cybersecurity Learning Platform (PROPRIETARY SOFTWARE)
 * Authentication Context
 * 
 * Copyright (c) 2025 Srivatsan Sk. All rights reserved.
 * 
 * PROPRIETARY LICENSE NOTICE:
 * This software is the exclusive proprietary property of Srivatsan Sk.
 * Licensed to client under strict commercial license agreement.
 * Unauthorized use, copying, modification, distribution, or removal
 * of copyright notices is strictly prohibited.
 * 
 * Original Author: Srivatsan Sk
 * License: Proprietary/Commercial
 * Status: Confidential
 * 
 * WARNING: This code contains proprietary business logic and algorithms.
 * Reverse engineering, decompilation, or derivative works are prohibited.
 */

// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/apiClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Load user from localStorage on startup
    useEffect(() => {
        const loadUser = async () => {
            setLoading(true);
            try {
                const savedUser = localStorage.getItem('user');
                const token = localStorage.getItem('accessToken');

                if (savedUser && token) {
                    // Set user from localStorage first
                    setCurrentUser(JSON.parse(savedUser));
                    
                    // Then fetch fresh user data from API to get latest reward_points and other info
                    try {
                        const freshUser = await api.users.getProfile();
                        setCurrentUser(freshUser);
                        localStorage.setItem('user', JSON.stringify(freshUser));
                    } catch (err) {
                        console.error('Error refreshing user data:', err);
                        // Keep using the saved user if refresh fails
                    }
                }
            } catch (err) {
                console.error('Error loading user:', err);
                setError('Failed to load user session');
            } finally {
                setLoading(false);
            }
        };

        loadUser();
    }, []);

    // Login function (keeps global loading untouched to avoid route flicker)
    const login = async (credentials) => {
        try {
            setError(null);

            const response = await api.auth.login(credentials);

            // Store tokens and user data - Updated to match new response format
            if (response.tokens && response.user) {
                localStorage.setItem('accessToken', response.tokens.access);
                localStorage.setItem('refreshToken', response.tokens.refresh);
                localStorage.setItem('user', JSON.stringify(response.user));

                setCurrentUser(response.user);

                // Check if password change is required
                if (response.user.password_change_required) {
                    return {
                        success: true,
                        passwordChangeRequired: true,
                        message: 'Password change required for security purposes'
                    };
                }

                return { success: true };
            } else {
                throw new Error('Invalid response format from server');
            }
        } catch (err) {
            console.error('Login error:', err);
            const errorMessage = err.response?.data?.detail ||
                err.response?.data?.message ||
                err.message ||
                'Failed to login. Please check your credentials.';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    };

    // Register function
    const register = async (userData) => {
        try {
            setLoading(true);
            setError(null);

            // Format data to match API expectations
            const formattedData = {
                email: userData.email,
                name: userData.fullName,
                college: userData.college || '',
                register_number: userData.registerNumber || '',
                phone_number: userData.phoneNumber ? userData.phoneNumber.replace(/\s/g, '') : '', // Remove spaces from phone number
                branch: userData.branch || '',
                semester: parseInt(userData.semester, 10) || 1,
                workshop_code: userData.workshopCode || '',
                password: userData.password,
                confirm_password: userData.confirmPassword
            };

            const response = await api.auth.register(formattedData);

            // Our registration doesn't auto-login, it returns an activation message
            return {
                success: true,
                message: response.message || 'Registration successful. Please check your email for activation instructions.'
            };
        } catch (err) {
            console.error('Register error:', err);
            // Handle different error formats
            let errorMessage = 'Registration failed. Please try again.';

            if (err.response?.data) {
                // DRF often returns validation errors as an object with field names as keys
                const errors = err.response.data;
                if (typeof errors === 'object' && !Array.isArray(errors)) {
                    // Check for non_field_errors first (global validation errors)
                    if (errors.non_field_errors && Array.isArray(errors.non_field_errors)) {
                        errorMessage = errors.non_field_errors.join(', ');
                    }
                    // Check for detail or message
                    else if (errors.detail || errors.message) {
                        errorMessage = errors.detail || errors.message;
                    }
                    // Handle field-specific errors
                    else {
                        const fieldErrors = [];
                        Object.keys(errors).forEach(field => {
                            const fieldError = Array.isArray(errors[field])
                                ? errors[field][0]
                                : errors[field];
                            fieldErrors.push(`${field.charAt(0).toUpperCase() + field.slice(1)}: ${fieldError}`);
                        });
                        if (fieldErrors.length > 0) {
                            errorMessage = fieldErrors.join(', ');
                        }
                    }
                } else if (errors.detail || errors.message) {
                    errorMessage = errors.detail || errors.message;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    // Logout function
    const logout = async () => {
        try {
            setLoading(true);
            const refreshToken = localStorage.getItem('refreshToken');

            // Call API logout endpoint if refresh token exists
            if (refreshToken) {
                try {
                    await api.auth.logout(refreshToken);
                } catch (err) {
                    console.error('API logout error:', err);
                    // Continue with local logout even if API call fails
                }
            }

            // Clear local storage
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');

            setCurrentUser(null);
            
            // Redirect to login page
            navigate('/login');
            
            return { success: true };
        } catch (err) {
            console.error('Logout error:', err);
            return { success: false, error: 'Failed to logout' };
        } finally {
            setLoading(false);
        }
    };

    // Update profile function
    const updateProfile = async (profileData) => {
        try {
            setLoading(true);
            setError(null);

            const updatedUser = await api.users.updateProfile(profileData);

            // Update local storage and state
            setCurrentUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));

            return { success: true };
        } catch (err) {
            console.error('Update profile error:', err);

            // Handle field-specific validation errors
            if (err.response?.status === 400 && err.response?.data) {
                const validationErrors = err.response.data;

                // Convert field errors to user-friendly messages
                const errorMessages = [];
                Object.entries(validationErrors).forEach(([field, errors]) => {
                    if (Array.isArray(errors)) {
                        errors.forEach(error => errorMessages.push(error));
                    } else {
                        errorMessages.push(errors);
                    }
                });

                const errorMessage = errorMessages.join('. ');
                setError(errorMessage);
                return { success: false, error: errorMessage, validationErrors };
            }

            // Handle generic errors
            const errorMessage = err.response?.data?.detail ||
                err.response?.data?.message ||
                err.message ||
                'Failed to update profile';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    // Change password function
    const changePassword = async (currentPassword, newPassword, isFromLogin = false) => {
        try {
            const response = await api.auth.changePassword({
                current_password: isFromLogin ? '' : currentPassword, // Skip current password for mandatory changes
                new_password: newPassword,
                from_login: isFromLogin
            });

            if (response.success) {
                // If this was a mandatory password change, update user state
                if (isFromLogin && currentUser) {
                    setCurrentUser({ ...currentUser, password_change_required: false });
                }
                return response;
            } else {
                throw new Error(response.message || 'Password change failed');
            }
        } catch (error) {
            console.error('Password change failed:', error);
            throw error;
        }
    };    // Check auth status - useful for protected routes
    const checkAuth = async () => {
        // If we have a user already, we're authenticated
        if (currentUser) return true;

        // Otherwise, try to get profile to verify token is valid
        try {
            const user = await api.users.getProfile();
            setCurrentUser(user);
            localStorage.setItem('user', JSON.stringify(user));
            return true;
        } catch (err) {
            // If we get an error, clear any stale auth data
            logout();
            return false;
        }
    };

    // Refresh user data from API (gets latest reward_points and other info)
    const refreshUser = async () => {
        try {
            const freshUser = await api.users.getProfile();
            setCurrentUser(freshUser);
            localStorage.setItem('user', JSON.stringify(freshUser));
            return freshUser;
        } catch (err) {
            console.error('Error refreshing user data:', err);
            throw err;
        }
    };

    // Auth context value
    const value = {
        currentUser,
        loading,
        error,
        login,
        register,
        logout,
        updateProfile,
        changePassword,
        checkAuth,
        refreshUser,
        isAuthenticated: !!currentUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook for using the auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
